import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, Lock, Sparkles, Compass, FileText, Dna, Rocket, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

export type QuestStage = {
  id: string;
  title: string;
  blurb: string;
  to: string;
  xp: number;
  done: boolean;
};

const ICONS = [Compass, Eye, FileText, Dna, Rocket];

/**
 * The learner journey, assessment → Next Steps, as a quest path.
 * Presentation only: stages are derived from real signals by the caller.
 */
export function LearnerQuest({ stages }: { stages: QuestStage[] }) {
  const reduce = useReducedMotion();
  const doneCount = stages.filter((s) => s.done).length;
  const currentIdx = stages.findIndex((s) => !s.done);
  const pct = Math.round((doneCount / stages.length) * 100);
  const earned = stages.filter((s) => s.done).reduce((n, s) => n + s.xp, 0);

  return (
    <section className="glass-panel p-5 sm:p-7" aria-labelledby="quest-heading">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Your quest</p>
          <h2 id="quest-heading" className="font-display mt-1 text-xl font-semibold sm:text-2xl">
            {doneCount === stages.length ? "Quest complete — keep growing" : `Stage ${currentIdx + 1} of ${stages.length}`}
          </h2>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-celebrate/10 px-3 py-1 text-sm font-semibold text-celebrate">
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          {earned} XP
        </span>
      </div>

      <div
        className="mt-4 h-2.5 overflow-hidden rounded-full bg-muted/60"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Journey progress"
      >
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-primary to-accent"
          initial={reduce ? false : { width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>

      <ol className="mt-6 space-y-3">
        {stages.map((s, i) => {
          const Icon = ICONS[i] ?? Compass;
          const current = i === currentIdx;
          const locked = !s.done && !current;
          const inner = (
            <>
              <span
                className={cn(
                  "relative grid h-11 w-11 shrink-0 place-items-center rounded-2xl transition-colors",
                  s.done && "bg-primary text-primary-foreground",
                  current && "bg-primary/15 text-primary ring-2 ring-primary/50",
                  locked && "bg-muted/50 text-muted-foreground",
                )}
              >
                {s.done ? <Check className="h-5 w-5" aria-hidden="true" /> : locked ? <Lock className="h-4 w-4" aria-hidden="true" /> : <Icon className="h-5 w-5" aria-hidden="true" />}
                {current && !reduce && (
                  <span className="absolute inset-0 animate-ping rounded-2xl ring-2 ring-primary/40" aria-hidden="true" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{s.title}</span>
                <span className="block text-sm text-muted-foreground">{s.blurb}</span>
              </span>
              <span className={cn("shrink-0 text-xs font-semibold", s.done ? "text-celebrate" : "text-muted-foreground")}>
                {s.done ? `+${s.xp}` : `${s.xp} XP`}
              </span>
              <span className="sr-only">{s.done ? "Done" : current ? "Up next" : "Coming up"}</span>
            </>
          );
          return (
            <motion.li
              key={s.id}
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07, duration: 0.35 }}
            >
              {locked ? (
                <div className="flex min-h-[64px] items-center gap-4 rounded-2xl px-3 py-2 opacity-70">{inner}</div>
              ) : (
                <Link
                  to={s.to}
                  className={cn(
                    "lift flex min-h-[64px] items-center gap-4 rounded-2xl px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                    current ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/30",
                  )}
                >
                  {inner}
                </Link>
              )}
            </motion.li>
          );
        })}
      </ol>
    </section>
  );
}
